import './hero.css';
import { RootState } from 'store';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import TopClient from './top-client';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';

const RevMemeGeneration = () => {
  const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);
  const navigate = useNavigate();

  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.6, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 1 } },
  };

  const topImageVariants = {
    hidden: (custom: 1 | 2) => ({
      y: -100,
      x: custom === 1 ? -50 : custom === 2 ? 50 : 0,
      opacity: 0,
    }),
    visible: (custom: 1 | 2) => ({
      y: 0,
      x: 0,
      scale: 1,
      opacity: 1,
      transition: { duration: 0.8, delay: custom * 0.2 },
    }),
  };

  const bottomImageVariants = {
    hidden: (custom: 1 | 2) => ({
      y: 100,
      x: custom === 1 ? -50 : custom === 2 ? 50 : 0,
      opacity: 0,
    }),
    visible: (custom: 1 | 2) => ({
      y: 0,
      x: 0,
      scale: 1,
      opacity: 1,
      transition: { duration: 0.8, delay: custom * 0.2 },
    }),
  };

  return (
    <div
      ref={ref}
      className="relative flex flex-col items-center justify-center py-16 h-[1060px] sm:h-screen sm:py-24 md:py-52 px-4 sm:px-6 overflow-hidden bg-background-marketing"
    >
      {isDarkMode ? (
        <>
          <div className="absolute inset-0 bg-radial-gradient z-10"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1AAA] via-transparent to-[#1A1A1AAA] z-10"></div>
        </>
      ) : (
        <></>
      )}

      {/* Background Images */}
      <motion.div
        className="absolute inset-0 z-0 max-w-[1200px] mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate={controls}
      >
        {/* Top Row - Left (Sheep/Cow) */}
        <motion.div
          className="absolute top-[12%] left-[20px] sm:-top-[5%] sm:left-48 w-[102px] h-[152px] sm:w-auto sm:h-[32%] sm:aspect-[2/3] rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl"
          variants={topImageVariants}
          custom={1}
          initial="hidden"
          animate={controls}
          whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
        >
          <img
            src="/meme-img-cat.png"
            className="w-full h-full object-cover"
            alt="Meme background 1"
          />
          <div className="absolute inset-0 bg-black/30"></div>
        </motion.div>

        {/* Top Row - Center (Wolf) */}
        <motion.div
          className="absolute top-[8%] left-[calc(50%-50px)] -translate-x-1/2 sm:-top-[5%] sm:right-48 sm:left-auto sm:-translate-x-0 w-[102px] h-[152px] sm:w-auto sm:h-[32%] sm:aspect-[2/3] rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl"
          variants={topImageVariants}
          custom={2}
          initial="hidden"
          animate={controls}
          whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
        >
          <img
            src="/meme-img-dog.png"
            className="w-full h-full object-cover"
            alt="Meme background 2"
          />
          <div className="absolute inset-0 bg-black/30"></div>
        </motion.div>

        {/* Top Row - Right (Dinosaur) */}
        <motion.div
          className="absolute top-[12%] right-[20px] sm:bottom-[calc(36%)] sm:left-20 sm:top-auto sm:right-auto w-[102px] h-[152px] sm:w-auto sm:h-[32%] sm:aspect-[2/3] rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl"
          variants={topImageVariants}
          custom={1}
          initial="hidden"
          animate={controls}
          whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
        >
          <img
            src="/turbo-bg.png" 
            className="w-full h-full object-cover"
            alt="Meme background 3"
          />
          <div className="absolute inset-0 bg-black/30"></div>
        </motion.div>

        {/* Bottom Row - Left (Wolf) */}
        <motion.div
          className="absolute bottom-[12%] left-[20px] sm:bottom-[calc(36%)] sm:right-20 sm:left-auto w-[102px] h-[152px] sm:w-auto sm:h-[32%] sm:aspect-[2/3] rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl"
          variants={bottomImageVariants}
          custom={1}
          initial="hidden"
          animate={controls}
          whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
        >
          <img
            src="/meme-img-bear.png"
            className="w-full h-full object-cover"
            alt="Meme background 4"
          />
          <div className="absolute inset-0 bg-black/30"></div>
        </motion.div>

        {/* Bottom Row - Center (Sheep/Cow) */}
        <motion.div
          className="absolute bottom-[8%] left-[calc(50%-50px)] -translate-x-1/2 sm:-bottom-[2%] sm:left-48 sm:-translate-x-0 w-[102px] h-[152px] sm:w-auto sm:h-[32%] sm:aspect-[2/3] rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl"
          variants={bottomImageVariants}
          custom={2}
          initial="hidden"
          animate={controls}
          whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
        >
          <img
            src="/neiro-bg.png"
            className="w-full h-full object-cover"
            alt="Meme background 1"
          />
          <div className="absolute inset-0 bg-black/30"></div>
        </motion.div>

        {/* Bottom Row - Right (Hedgehog) */}
        <motion.div
          className="absolute bottom-[12%] right-[20px] sm:-bottom-[2%] sm:right-48 w-[102px] h-[152px] sm:w-auto sm:h-[32%] sm:aspect-[2/3] rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl"
          variants={bottomImageVariants}
          custom={1}
          initial="hidden"
          animate={controls}
          whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
        >
          <img
            src="/meme-img-hedgehog.png"
            className="w-full h-full object-cover"
            alt="Meme background 2"
          />
          <div className="absolute inset-0 bg-black/30"></div>
        </motion.div>
      </motion.div>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center relative z-20 max-w-full mt-[-40px] sm:mt-[-60px]">
        <div className="text-text-secondary text-sm mb-5">
          TRUSTED BY TOP CLIENTS
        </div>
        <TopClient />

        <div className="mt-6 sm:mt-8 font-normal">
          <h2 className="text-3xl md:text-[48px] text-center leading-tight text-text-primary">
            Revolutionizing
          </h2>
          <h2 className="text-3xl md:text-[48px] text-center meme-generation-text leading-tight">
            Meme Generation
          </h2>
        </div>

        <div className="mt-4 sm:mt-6 text-center text-sm sm:text-base md:text-lg max-w-[85%] md:max-w-[520px] mx-auto text-text-secondary">
          Top clients like Neiro and Sundog, we blend humor and tech to drive
          connections. Backed by six strategic investors, we&apos;re redefining
          creative AI for the crypto world.
        </div>

        <Button
          className="mt-4 sm:mt-6 text-primary hover:bg-secondary"
          size="lg"
          variant="secondary"
          onClick={() => navigate('/contact')}
        >
          Contact Us
        </Button>
      </div>
    </div>
  );
};

export default RevMemeGeneration;
